import { CircularProgress } from '@mui/material';
import * as React from 'react';
import * as IdentityHelper from './api/identity/identityHelper';
import * as ProductHelper from './api/product/productHelper';
import * as SoilHelper from './api/soil/soilHelper';
import { ISrcImage } from './design/engine/Index';
import { loadImages } from './design/engine/Utils';
import drainPattern from './design/engine/image/drainPattern.svg';
import generalViewDoubleWithSecondaryDrain from './design/engine/image/generalViewDoubleWithSecondaryDrain.png';
import generalViewDoubleWithoutSecondaryDrain from './design/engine/image/generalViewDoubleWithoutSecondaryDrain.png';
import generalViewSingleWithSecondaryDrain from './design/engine/image/generalViewSingleWithSecondaryDrain.png';
import generalViewSingleWithoutSecondaryDrain from './design/engine/image/generalViewSingleWithoutSecondaryDrain.png';
import groundwaterPattern from './design/engine/image/groundwaterPattern.svg';
import impermeableLayerPattern from './design/engine/image/impermeableLayerPattern.svg';
import waterPattern from './design/engine/image/waterPattern.svg';
import route, { RouteProvider } from './route/Index';
import { ENodeEnv, ERole } from './shared/enum';
import { IUser } from './shared/iUser';
import variable from './shared/style/variable';
import { IResultCalculationImage, selectImage } from './store/Image/imageSlice';
import { EReduxState } from './store/enum';
import { useAppDispatch, useAppSelector } from './store/hook';
import { selectProductPreview, setProductPreview } from './store/product/productPreviewSlice';
import { selectSoil, setSoil } from './store/soil/soilSlice';
import { selectUser, setUser } from './store/user/userSlice';

interface IAppProps {}

export const HtmlImageContext = React.createContext<ISrcImage | null>(null);

const App = (props: IAppProps) => {
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectUser);
    const productPreview = useAppSelector(selectProductPreview);
    const soil = useAppSelector(selectSoil);
    const image = useAppSelector(selectImage);

    let [htmlImgObj, setHtmlImgObj] = React.useState<ISrcImage>();

    const fetchUser = async () => {
        let user: IUser;
        if (process.env.NODE_ENV == ENodeEnv.Production) user = await IdentityHelper.getUserAsync();
        else user = { email: 'test@gmail.com', firstName: 'Mario', lastName: 'Rossi', isAdmin: true, role: ERole.Admin };
        dispatch(setUser(user));
    };

    const fetchProductPreview = async () => {
        let res = await ProductHelper.getPaginatedProductPreviewsAsync();
        dispatch(setProductPreview(res.data.value));
    };

    const fetchSoil = async () => {
        let res = await SoilHelper.getAllSoilAsync();
        dispatch(setSoil(res.data));
    };

    const loadStaticImage = () => {
        var src: ISrcImage = {
            generalViewDoubleWithSecondaryDrain,
            generalViewDoubleWithoutSecondaryDrain,
            generalViewSingleWithSecondaryDrain,
            generalViewSingleWithoutSecondaryDrain,
            drainPattern,
            waterPattern,
            impermeableLayerPattern,
            groundwaterPattern,
        };

        loadImages(src, (htmlObj: ISrcImage) => setHtmlImgObj(htmlObj));
    };

    React.useEffect(() => {
        loadStaticImage();
        fetchUser();
    }, []);

    React.useEffect(() => {
        if (user.status == EReduxState.fulfilled) {
            fetchProductPreview();
            fetchSoil();
        }
    }, [JSON.stringify(user.data)]);

    React.useEffect(() => {
        if (
            image.data?.resultCalculation.axis &&
            image.data?.resultCalculation.preFsStatic &&
            image.data?.resultCalculation.postFsStatic &&
            image.data?.resultCalculation.preFsSeismic &&
            image.data?.resultCalculation.postFsSeismic
        ) {
            let src: IResultCalculationImage = image.data?.resultCalculation!;
            loadImages(src, (htmlResultCalculationObj: any) => setHtmlImgObj({ ...htmlImgObj, resultCalculation: htmlResultCalculationObj }));
        }
    }, [JSON.stringify(image.data?.resultCalculation)]);

    return user.status == EReduxState.fulfilled && htmlImgObj && productPreview.status == EReduxState.fulfilled && soil.status == EReduxState.fulfilled ? (
        <HtmlImageContext.Provider value={htmlImgObj}>
            <RouteProvider router={route} />
        </HtmlImageContext.Provider>
    ) : (
        <CircularProgress style={{ position: 'absolute', top: '50%', left: '47%', color: variable.green }} />
    );
};

export default App;
